import type { SessionDataType } from '@confluence/session-data';

import { initializeClientFromValues } from './initializeClientFromValues';
import { initializeClientWithProvider } from './initializeClientWithProvider';
import type { StatsigBootstrapData } from './getStatsigBootstrap';

export type StatsigOptions = {
	userId: SessionDataType['userId'];
	cloudId: SessionDataType['cloudId'] | null;
	orgId: SessionDataType['orgId'];
	environment: SessionDataType['environment'] | string;
	bootstrappedOptions?: StatsigBootstrapData;
};

/**
 * Initializes the Statsig client.
 *
 * You can optionally provide the `bootstrappedOptions` param if the data was preloaded (as is the case during SSR, for example).
 * If the bootstrapped data is not provided, the data will be fetched.
 */
export const initialize = async ({
	userId,
	cloudId = '',
	orgId,
	environment,
	bootstrappedOptions,
}: StatsigOptions): Promise<void> => {
	if (bootstrappedOptions) {
		const { clientSdkKey, experimentValues, configCollectionValues, persistedStatsigOverrides } =
			bootstrappedOptions;
		// Although this function returns a promise, it is actually synchronous when experimentValues are provided
		await initializeClientFromValues({
			experimentValues,
			clientSdkKey,
			identifiers: {
				userId,
				tenantId: cloudId || '',
				orgId,
			},
			environment,
			configCollectionValues,
			persistedStatsigOverrides,
		});
	} else {
		await initializeClientWithProvider({
			cloudId,
			userId,
			environment,
		});
	}
};
